// LoginWall.tsx — the hard sign-in gate. When activation is on and there is no
// session, the app is NOT enterable. It opens on a calm, branded WELCOME view
// (logo + wordmark + warm intro); "Get started" / "I already have an account"
// reveal the sign-in panel. Once a session lands, useAuth re-renders past this wall.
import React from 'react';
import { Display, Body, Btn, Pressable } from '@/components/ui';
import { Icon, Wordmark } from '@/components/icons';
import { AccountSignIn } from '@/components/AccountSignIn';

const wall: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 130,
  background: 'var(--cream)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  padding: 'calc(var(--safe-top) + 32px) 30px calc(var(--safe-bottom) + 26px)',
};

export function LoginWall() {
  // 'create' collects a first name (new account); 'signin' is a returning user (no name).
  const [view, setView] = React.useState<'welcome' | 'create' | 'signin'>('welcome');

  if (view === 'welcome') {
    return (
      <div style={{ ...wall, justifyContent: 'center' }}>
        <div className="fade-up" style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Wordmark h={30} />
          {/* Free-trial emphasis: the headline reassurance, up top. */}
          <span
            style={{
              marginTop: 24,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '7px 15px',
              borderRadius: 999,
              background: 'rgba(200,134,26,0.12)',
              border: '1px solid rgba(200,134,26,0.28)',
              color: 'var(--amber-deep)',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 12.5,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            <Icon name="leaf" size={14} /> Free 10-day trial
          </span>

          <Display parts={{ pre: 'Welcome to ', em: 'ReNurture', post: '.' }} size={29} lh={1.16} style={{ marginTop: 18 }} />

          <Body size={15.5} style={{ margin: '16px auto 0', maxWidth: 330, lineHeight: 1.66 }}>
            A calm, clinician-built space to recover from a difficult relationship, at your own pace and in your own time.
          </Body>

          {/* What the trial gives: every feature, plus the opening chapters. The
              full programme is membership (content is capped at chapter 4). */}
          <Body size={15} style={{ margin: '22px auto 0', maxWidth: 330, lineHeight: 1.7 }}>
            Your 10-day trial unlocks{' '}
            <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>every feature</strong>: all the tools, the
            meditations, and the opening chapters of the programme. Become a member any time for the full programme.
          </Body>

          {/* The keep-forever gift. */}
          <Body size={15} style={{ margin: '14px auto 0', maxWidth: 330, lineHeight: 1.7 }}>
            And the self-compassion course is yours to keep, free, as our gift, even after the trial ends.
          </Body>

          <Body dim size={12.5} style={{ margin: '22px auto 0', maxWidth: 320, lineHeight: 1.6 }}>
            Create a free account to begin, so your progress, reflections and saved items are always saved to you.
          </Body>

          <div style={{ width: '100%', marginTop: 30 }}>
            <Btn full size="lg" onClick={() => setView('create')} leading={<Icon name="leaf" size={18} />}>Create my free account</Btn>
          </div>
          <Pressable
            as="button"
            onClick={() => setView('signin')}
            ariaLabel="I already have an account"
            style={{
              marginTop: 14, background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, fontWeight: 600, color: 'var(--green-deep)', padding: '8px 12px',
            }}
          >
            I already have an account
          </Pressable>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...wall, justifyContent: 'flex-start' }}>
      <div style={{ width: '100%', maxWidth: 340, display: 'flex', justifyContent: 'flex-start' }}>
        <Pressable
          as="button"
          onClick={() => setView('welcome')}
          ariaLabel="Back"
          style={{ width: 40, height: 40, borderRadius: 999, border: '1px solid var(--hair)', background: 'var(--card-top)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--edge), var(--sh1)' }}
        >
          <Icon name="chevLeft" size={18} />
        </Pressable>
      </div>
      <div style={{ marginTop: 10, marginBottom: 18 }}>
        <Wordmark h={28} />
      </div>
      <Display
        parts={view === 'create'
          ? { pre: 'Create your ', em: 'free account', post: '.' }
          : { pre: 'Sign in to ', em: 'continue', post: '.' }}
        size={25}
        lh={1.18}
      />
      <Body size={15} style={{ margin: '14px auto 0', maxWidth: 330, lineHeight: 1.66 }}>
        {view === 'create'
          ? 'Your free 10-day trial unlocks every feature, and the self-compassion course stays yours to keep, free, afterwards.'
          : 'Welcome back. Enter your email and we’ll send you a sign-in code.'}
      </Body>
      <div style={{ marginTop: 18, width: '100%', maxWidth: 340 }}>
        <AccountSignIn mode={view === 'create' ? 'create' : 'signin'} />
      </div>
    </div>
  );
}
